import { FollowType } from "@product/scmp-sdk";
import first from "lodash/first";
import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import type { ContentItemRenderVariantProps } from "~/components/content/content-item-render/types";
import type { homeHeroArticleContentItemContent$key } from "~/queries/__generated__/homeHeroArticleContentItemContent.graphql";

import {
  ActionBar,
  Container,
  Content,
  CoverImage,
  Headline,
  LinkHighlightArea,
  MoreOnThis,
  StyledContentHeadline,
  StyledEntityLink,
  StyledHomeFollowButton,
  Summary,
  TopicLink,
} from "./styles";

export type Props = {
  reference: homeHeroArticleContentItemContent$key;
} & ContentItemRenderVariantProps;

export const ContentItemHomeHeroArticle = forwardRef<HTMLDivElement, Props>(
  ({ className, onClick, provider, reference: reference_ }, reference) => {
    const content = useFragment(
      graphql`
        fragment homeHeroArticleContentItemContent on Article {
          entityId
          moreOnThisArticles {
            entityId
            ... on Article {
              ...contentHeadlineContent
              ...entityLink
            }
          }
          topics {
            ...followButtonBase
          }
          ...contentCoverImageContent
          ...contentTopicLinkContent
          ...entityLink
        }
      `,
      reference_,
    );
    const moreOnThisArticle = first(content.moreOnThisArticles) ?? undefined;
    const topic = first(content.topics) ?? undefined;

    return (
      <Container className={className} ref={reference}>
        <CoverImage>
          <StyledEntityLink reference={content} onClick={() => onClick?.(content.entityId)}>
            {provider.coverImage({
              responsiveVariants: {
                desktopUp: "size1200x800",
                mobileUp: "size540x360",
                tabletUp: "size1200x800",
              },
              withCaption: true,
            })}
          </StyledEntityLink>
        </CoverImage>

        <Content>
          <StyledEntityLink reference={content} onClick={() => onClick?.(content.entityId)}>
            <LinkHighlightArea>
              <Headline>
                {provider.headline({
                  hideTooltip: true,
                  preferSocialHeadline: true,
                })}
                {provider.liveTag()}
              </Headline>
              <Summary>
                {provider.summary({
                  isTextOverflowEllipsis: true,
                })}
              </Summary>
            </LinkHighlightArea>
          </StyledEntityLink>

          <ActionBar>
            {provider.actionBar({
              commentVariant: "reverse",
              isBookmarkVisible: false,
              isCommentsVisible: true,
            })}
          </ActionBar>

          {moreOnThisArticle && (
            <MoreOnThis>
              <StyledEntityLink
                reference={moreOnThisArticle}
                onClick={() => onClick?.(moreOnThisArticle.entityId)}
              >
                <StyledContentHeadline
                  hideTag={false}
                  preferSocialHeadline={true}
                  reference={moreOnThisArticle}
                  skipHighlight={true}
                />
              </StyledEntityLink>
            </MoreOnThis>
          )}

          <TopicLink>
            {provider.topicLink?.()}
            {topic && (
              <StyledHomeFollowButton
                reference={topic}
                source="Top Story 1"
                type={FollowType.Topic}
                isLoadingEnabled
              />
            )}
          </TopicLink>
        </Content>
      </Container>
    );
  },
);

ContentItemHomeHeroArticle.displayName = "ContentItemHomeHeroArticle";
